import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { animated, useSpring } from "@react-spring/web";
import { Button, Flex, Layout, Tooltip } from "antd";
import { CompanyMenu } from "app/CompanyMenu";
import { Logo } from "components/Logo";
import { useReducedMotion } from "hooks/useReducedMotion";
import { useState } from "react";
import { AppMenu } from "./AppMenu";
import { useSidebar } from "./SidebarContext";
import { SIDER_WIDTH } from "./siderLayout";

export function AppSider() {
	const { collapsed, setCollapsed } = useSidebar();
	const reduced = useReducedMotion();
	/**
	 * A responsive OVERRIDE, deliberately separate from the stored preference.
	 *
	 * 🪤 `setCollapsed` writes to localStorage. Driving it from `onBreakpoint`
	 * would make one visit on a phone silently collapse the user's desktop
	 * sidebar on their next session. The breakpoint forces the rail; it never
	 * records an opinion.
	 */
	const [narrow, setNarrow] = useState(false);
	const railed = collapsed || narrow;

	const logoSpring = useSpring({
		opacity: 1,
		transform: railed ? "scale(0.9)" : "scale(1)",
		config: { tension: 200, friction: 20 },
		immediate: reduced,
		reset: true,
	});

	return (
		// 🚨 Without a breakpoint this Sider held a FIXED 250px at every width, so
		// `<main>` measured 140px at a 390px viewport on EVERY route — content
		// crushed, not scrolled, with every word wrapping to a character or two.
		// Measured before: main 140px. After: 310px.
		<Layout.Sider
			width={SIDER_WIDTH}
			collapsedWidth={80}
			collapsible
			breakpoint="lg"
			onBreakpoint={setNarrow}
			collapsed={railed}
			trigger={null}
			style={siderStyle}
		>
			<Flex vertical gap={10} style={wrapperStyle}>
				<Flex style={logoBlockStyle} justify="space-between" align="center">
					<animated.div style={logoSpring}>
						{railed ? (
							<Logo width={37} small />
						) : (
							<Logo width={100} key="full_logo" />
						)}
					</animated.div>
				</Flex>
				<Flex vertical gap={10} style={contantStyle}>
					<CompanyMenu />
					<AppMenu />
				</Flex>
				<Flex justify="center" style={{ paddingBottom: 8 }}>
					<Tooltip
						title={railed ? "Click to expand sidebar" : undefined}
						placement="right"
						trigger={["hover", "focus"]}
					>
						<Button
							type="text"
							icon={railed ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
							onClick={() => setCollapsed(!railed)}
							aria-label={railed ? "Expand sidebar" : "Collapse sidebar"}
							aria-expanded={!railed}
							style={{
								color: "var(--color-text-secondary)",
								minWidth: 44,
								minHeight: 44,
							}}
						/>
					</Tooltip>
				</Flex>
			</Flex>
		</Layout.Sider>
	);
}

const siderStyle: React.CSSProperties = {
	borderRight: "1px solid var(--color-border)",
	// Matches the content pane: the support-access bar sits above both, and this
	// is the only other 100vh pane. Defaults to 0px when no session is active.
	height: "calc(100vh - var(--support-access-bar-height, 0px))",
	position: "sticky",
	top: 0,
	overflow: "hidden",
};

const wrapperStyle: React.CSSProperties = {
	height: "100%",
	padding: "16px 0",
};

const contantStyle: React.CSSProperties = {
	flex: 1,
	minHeight: 0,
	overflowY: "auto",
	padding: "0 10px",
};

const logoBlockStyle: React.CSSProperties = {
	flexShrink: 0,
	minWidth: 100,
	marginLeft: 10,
	marginRight: 10,
	paddingLeft: 10,
	paddingBottom: 8,
	borderBottom: "1px solid var(--color-border)",
	overflow: "hidden",
};
